import { Show } from 'solid-js'

import { useI18n } from '../../i18n'
import {
   setMeasurementsEffortBucket,
   toggleMeasurementsChartValue,
} from '../../state/measurementsUiStore'
import BucketSizeChips from './BucketSizeChips'
import { formatDistance, formatEffort, formatMetricValue } from './measurementFormat'
import {
   type BucketSize,
   type EffortBucketSize,
   getEffortMonthCount,
   type TimelinePoint,
   type VolumeBucket,
} from './metricViewUtils'
import TimelineLineChart from './TimelineLineChart'
import VolumeBarChart from './VolumeBarChart'

type MetricChartsProps = {
   points: TimelinePoint[]
   range: { rangeStart: number; rangeEnd: number }
   /** The derived key the timeline plots (`pace` or `speed`). */
   valueKey: string
   buckets: VolumeBucket[]
   bucketSize: BucketSize
   /** Effort per bucket; empty when the metric has no effort score. */
   effortBuckets: VolumeBucket[]
   /** The effort chart's own bucket size (defaults to month, like the target). */
   effortBucketSize: EffortBucketSize
   /** Effort target per bucket (monthly target prorated to the bucket size), or null. */
   effortTarget: number | null
}

/**
 * Renders the value timeline (click toggles pace/speed), the volume bars, and
 * — for metrics with an effort score — the effort bars with the target guide,
 * each with its caption.
 *
 * @param props - Chart data and the active value key.
 * @returns The charts element.
 */
const MetricCharts = (props: MetricChartsProps) => {
   const t = useI18n()

   return (
      <>
         <div class="space-y-2">
            <TimelineLineChart
               points={props.points}
               rangeStart={props.range.rangeStart}
               rangeEnd={props.range.rangeEnd}
               formatValue={value => formatMetricValue(props.valueKey, value)}
               label={t('{value} over time', { value: t(props.valueKey) })}
               onToggle={toggleMeasurementsChartValue}
               toggleHint={t('Click to switch between pace and speed.')}
            />
            <p class="text-xs text-ink-faint">
               {t('{value} over time', { value: t(props.valueKey) })} ·{' '}
               {t('Click to switch between pace and speed.')}
            </p>
         </div>
         <div class="space-y-2">
            <VolumeBarChart
               buckets={props.buckets}
               bucketSize={props.bucketSize}
               formatTotal={total => formatDistance(total, 1)}
               label={t('Distance per {bucket}', { bucket: t(props.bucketSize) })}
            />
            <p class="text-xs text-ink-faint">
               {t('Distance per {bucket}', { bucket: t(props.bucketSize) })}
            </p>
         </div>
         <Show when={props.effortBuckets.length > 0}>
            <div class="space-y-2">
               <BucketSizeChips
                  value={props.effortBucketSize}
                  onChange={setMeasurementsEffortBucket}
               />
               <VolumeBarChart
                  buckets={props.effortBuckets}
                  bucketSize={props.effortBucketSize}
                  formatTotal={formatEffort}
                  formatTooltip={total => formatMetricValue('effort', total)}
                  label={t('Effort per {bucket}', { bucket: t(props.effortBucketSize) })}
                  target={props.effortTarget}
                  targetLabel={t('Target')}
                  splitAt={props.effortTarget}
                  visibleBucketCount={
                     props.effortBucketSize === 'month' ? getEffortMonthCount : undefined
                  }
                  stacked
               />
               <p class="text-xs text-ink-faint">
                  {t('Effort per {bucket}', { bucket: t(props.effortBucketSize) })}
                  <Show when={props.effortTarget !== null}>
                     {' · '}
                     {t('Target {value} per {bucket}', {
                        value: formatMetricValue('effort', props.effortTarget ?? 0),
                        bucket: t(props.effortBucketSize),
                     })}
                  </Show>
               </p>
            </div>
         </Show>
      </>
   )
}

export default MetricCharts
